import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { IBroadcast } from 'modules/core/supabase/tables/broadcast';
import { MatchEvent } from 'shared/types/match-event';

export type OverlaySliceState = {
  isLoading: boolean;
  isLoaded: boolean;

  broadcastId?: number;
  deviceId?: number;
  tournamentId?: number;
  championshipId?: number;
  matchId?: number;
  templateId: number | null;

  featureIsAvailable?: boolean;
  isBoardLocked: boolean;
  isHideControlPanel: boolean;
  isMatchAvailable: boolean | undefined;
  isBroadcastInitialized: boolean;
  isMatchDataReady: boolean;

  events: MatchEvent[];
  passedEventsId: number[];
  activeEventId: number | null;

  /**
   * Для каких табло с панели открыты окна: `null` — основное окно, число — id табло.
   * Сами ссылки на окна живут в `PopupRegistryProvider` — это ресурс, а не состояние.
   */
  openPopupBoardIds: (number | null)[];
  popupIsLoaded: boolean;
};

const initialState: OverlaySliceState = {
  isLoading: false,
  isLoaded: false,
  isBoardLocked: false,
  isHideControlPanel: false,
  isMatchAvailable: undefined,
  isBroadcastInitialized: false,
  isMatchDataReady: false,

  templateId: null,

  events: [],
  passedEventsId: [],
  activeEventId: null,

  openPopupBoardIds: [],
  popupIsLoaded: false,
};

export const overlaySlice = createSlice({
  name: 'overlay-v2',
  initialState,
  reducers: {
    setEvents(state, action: { payload: MatchEvent[] }) {
      state.events = action.payload;
    },
    addEvents(state, action: PayloadAction<MatchEvent[]>) {
      const events = action.payload.filter((event) => !state.passedEventsId.includes(event.id));

      state.events.push(...events);
      state.passedEventsId.push(...events.map((event) => event.id));
    },
    /**
     * Событие удалили в приложении — убираем его отовсюду, где оно ещё может всплыть.
     *
     * Из `passedEventsId` тоже: список отмечает «это уже видели», и оставленный там id
     * молча проглотил бы событие, если оператор вернёт его обратно в хронологию.
     */
    removeEvents(state, action: PayloadAction<number[]>) {
      const removed = action.payload;
      if (removed.length === 0) return;

      state.events = state.events.filter((event) => !removed.includes(event.id));
      state.passedEventsId = state.passedEventsId.filter((id) => !removed.includes(id));
      if (state.activeEventId !== null && removed.includes(state.activeEventId)) {
        state.activeEventId = null;
      }
    },
    shiftEvent(state) {
      state.events = state.events.slice(1);
    },
    setBoardLocked(state, action: PayloadAction<boolean>) {
      state.isBoardLocked = action.payload;
    },
    setHideControlPanel(state, action: PayloadAction<boolean>) {
      state.isHideControlPanel = action.payload;
    },
    setActiveEventId(state, action: PayloadAction<number | null>) {
      state.activeEventId = action.payload;
    },
    setPassedEventsId(state, action: PayloadAction<number[]>) {
      state.passedEventsId = action.payload;
    },
    setOpenPopupBoardIds(state, action: PayloadAction<(number | null)[]>) {
      state.openPopupBoardIds = action.payload;
    },
    setPopupIsLoaded(state, action: PayloadAction<boolean>) {
      state.popupIsLoaded = action.payload;
    },
    setIsLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setIsLoaded(state, action: PayloadAction<boolean>) {
      state.isLoaded = action.payload;
    },
    setIsMatchAvailable(state, action: PayloadAction<boolean | undefined>) {
      state.isMatchAvailable = action.payload;
    },
    setIsBroadcastInitialized(state, action: PayloadAction<boolean>) {
      state.isBroadcastInitialized = action.payload;
    },
    setIsMatchDataReady(state, action: PayloadAction<boolean>) {
      state.isMatchDataReady = action.payload;
    },
    setBroadcastState(state, action: PayloadAction<IBroadcast | undefined>) {
      if (!action.payload) {
        state.broadcastId = undefined;
        state.deviceId = undefined;
        state.tournamentId = undefined;
        state.championshipId = undefined;
        state.matchId = undefined;
        state.templateId = null;
        return;
      }

      const newMatchId = action.payload.matchId ?? undefined;
      if (state.matchId !== newMatchId) {
        state.isMatchDataReady = false;
        state.passedEventsId = [];
        state.events = [];
        state.activeEventId = null;
      }

      state.broadcastId = action.payload.id ?? undefined;
      state.deviceId = action.payload.deviceId ?? undefined;
      state.tournamentId = action.payload.tournamentId ?? undefined;
      state.championshipId = action.payload.championshipId ?? undefined;
      state.matchId = newMatchId;
      state.templateId = action.payload.templateId ?? null;
    },
  },
});

export const {
  setEvents,
  setBoardLocked,
  setHideControlPanel,
  addEvents,
  removeEvents,
  shiftEvent,
  setActiveEventId,
  setPassedEventsId,
  setOpenPopupBoardIds,
  setPopupIsLoaded,
  setIsLoading,
  setIsLoaded,
  setBroadcastState,
  setIsMatchAvailable,
  setIsBroadcastInitialized,
  setIsMatchDataReady,
} = overlaySlice.actions;
